import React, { useState, useEffect, useRef } from 'react';
import './WinampPlayer.css';

const WinampEmbedPlayer = ({ playlistUri = '37i9dQZF1DXcBWIGoYBM5M' }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(50);
  const [showPlaylist, setShowPlaylist] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const timeIntervalRef = useRef(null);

  // Spotify Embed URL'i oluştur
  const getEmbedUrl = () => {
    const id = playlistUri.includes('spotify:')
      ? playlistUri.split(':').pop()
      : playlistUri;

    let type = 'playlist';
    if (playlistUri.includes('track')) type = 'track';
    if (playlistUri.includes('album')) type = 'album';

    return `https://open.spotify.com/embed/${type}/${id}?utm_source=generator&theme=0`;
  };

  // Zaman sayacı
  useEffect(() => {
    if (isPlaying) {
      timeIntervalRef.current = setInterval(() => {
        setCurrentTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
      }
    }

    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
      }
    };
  }, [isPlaying]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (e) => {
    setVolume(parseInt(e.target.value));
  };

  const togglePlaylist = () => {
    setShowPlaylist(!showPlaylist);
  };

  return (
    <div className="winamp-wrapper">
      <div className="winamp-container">
        <div className="winamp-titlebar">
          <div className="winamp-title">
            <span className="winamp-icon">♪</span>
            Winamp v5.666
          </div>
          <div className="winamp-controls">
            <button className="titlebar-btn" title="Minimize">_</button>
            <button className="titlebar-btn" title="Maximize">□</button>
            <button className="titlebar-btn" title="Close">×</button>
          </div>
        </div>

        <div className="winamp-main">
          {/* Display Area */}
          <div className="winamp-display">
            <div className="display-screen">
              <div className="display-time">{formatTime(currentTime)}</div>
              <div className="display-visualizer">
                {[...Array(20)].map((_, i) => (
                  <div
                    key={i}
                    className="visualizer-bar"
                    style={{
                      height: isPlaying ? `${Math.random() * 100}%` : '2px'
                    }}
                  />
                ))}
              </div>
            </div>
            <div className="display-info">
              <div className="display-bitrate">128</div>
              <div className="display-khz">44</div>
              <div className="display-stereo">stereo</div>
            </div>
          </div>

          {/* Track Info */}
          <div className="winamp-track-info">
            <marquee className="track-title" behavior="scroll" scrollamount="2">
              {isPlaying ? '▶ Spotify Playlist Playing' : '⏸ Ready to play'}
            </marquee>
          </div>

          {/* Seek Bar */}
          <div className="winamp-seekbar">
            <input
              type="range"
              min="0"
              max="100"
              defaultValue="0"
              className="seekbar-slider"
            />
          </div>

          {/* Control Buttons */}
          <div className="winamp-buttons">
            <button className="control-btn" title="Previous">
              <span className="btn-icon">⏮</span>
            </button>
            <button className="control-btn play-btn" onClick={togglePlay} title={isPlaying ? 'Pause' : 'Play'}>
              <span className="btn-icon">{isPlaying ? '⏸' : '▶'}</span>
            </button>
            <button className="control-btn" onClick={togglePlay} title="Stop">
              <span className="btn-icon">⏹</span>
            </button>
            <button className="control-btn" title="Next">
              <span className="btn-icon">⏭</span>
            </button>
            <button className="control-btn" onClick={togglePlaylist} title="Toggle Playlist">
              <span className="btn-icon">📂</span>
            </button>
          </div>

          {/* Volume Control */}
          <div className="winamp-volume">
            <label>Volume</label>
            <input
              type="range"
              min="0"
              max="100"
              value={volume}
              onChange={handleVolumeChange}
              className="volume-slider"
            />
            <span className="volume-value">{volume}%</span>
          </div>

          {/* Equalizer Display */}
          <div className="winamp-equalizer">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="eq-band">
                <div
                  className="eq-slider"
                  style={{
                    height: isPlaying ? `${30 + Math.random() * 40}%` : '50%'
                  }}
                />
                <div className="eq-label">{['60', '170', '310', '600', '1K', '3K', '6K', '12K', '14K', '16K'][i]}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="winamp-footer">
          <div className="winamp-status">
            {isPlaying ? '▶ PLAYING' : '⏸ PAUSED'} | Click 📂 to {showPlaylist ? 'hide' : 'show'} playlist
          </div>
        </div>
      </div>

      {/* Playlist Window (Spotify Embed) */}
      {showPlaylist && (
        <div className="winamp-playlist-window">
          <div className="playlist-titlebar">
            <div className="playlist-title">
              <span className="winamp-icon">🎵</span>
              Playlist Editor
            </div>
            <div className="winamp-controls">
              <button className="titlebar-btn" onClick={togglePlaylist}>_</button>
              <button className="titlebar-btn">□</button>
              <button className="titlebar-btn" onClick={togglePlaylist}>×</button>
            </div>
          </div>
          <div className="playlist-content">
            <iframe
              src={getEmbedUrl()}
              width="100%"
              height="100%"
              frameBorder="0"
              allowFullScreen=""
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              title="Spotify Player"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WinampEmbedPlayer;
